import { AnchorTargetTypes } from "@rsm-hcd/javascript-core";
import Anchor from "atoms/anchors/anchor";
import Trans from "atoms/i18n/trans";
import Paragraph from "atoms/typography/paragraph";
import { Casi, NfpaWithHtml } from "constants/app-name-tm";
import { siteMap } from "internal-sitemap";
import { ModalTypes } from "molecules/constants/modal-types";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import BaseModalHeader from "molecules/modals/headers/base-modal-header";
import Modal from "molecules/modals/modal";
import SupportFaqPanel from "pages/support/support-faq-panel";
import React from "react";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import { useLocalization } from "utilities/hooks/use-localization";
import CultureResources from "utilities/interfaces/culture-resources";
import { RouteUtils } from "utilities/route-utils";

interface AboutCasiModalProps {
    closeDialog: () => void;
    isVisible: boolean;
}

type Config = {
    questionKey: keyof CultureResources;
    answerKey: keyof CultureResources;
    components?: Record<string, React.ReactElement>;
};

const ANCHOR_CLASS_NAME = "-anchor";
const ANSWER_CLASS_NAME = "-answer";
const U_LIST_CLASS_NAME = "-u-list";
const TRADEMARK_CLASS_NAME = "-trademark";

const AboutCasiModal: React.FC<AboutCasiModalProps> = ({
    closeDialog,
    isVisible,
}) => {
    const { t } = useLocalization();

    const label = t("aboutApp", { appName: Casi });

    const defaultComponents = {
        li: <li />,
        p: <Paragraph cssClassName={ANSWER_CLASS_NAME} />,
        sup: <sup className={TRADEMARK_CLASS_NAME} />,
        ul: <ul className={U_LIST_CLASS_NAME} />,
    };

    const renderPanels = (configs: Config[]) =>
        configs.map(({ questionKey, answerKey, components }) => (
            <SupportFaqPanel
                buttonText={t(questionKey)}
                key={questionKey}
                cultureKey={questionKey}
                translationValues={{ casi: Casi, nfpa: NfpaWithHtml }}>
                <Trans
                    i18nKey={answerKey}
                    components={{ ...defaultComponents, ...components }}
                    values={{ casi: Casi, nfpa: NfpaWithHtml }}
                />
            </SupportFaqPanel>
        ));

    const panelConfigs: Config[] = [
        {
            questionKey: "about-casi-content",
            answerKey: "about-casi-content-answer",
        },
        {
            questionKey: "about-casi-questions",
            answerKey: "about-casi-questions-answer",
        },
        {
            questionKey: "about-casi-citations",
            answerKey: "about-casi-citations-answer",
        },
        {
            questionKey: "about-casi-not-good",
            answerKey: "about-casi-not-good-answer",
        },
        {
            questionKey: "about-casi-future",
            answerKey: "about-casi-future-answer",
        },
        {
            questionKey: "about-casi-learn",
            answerKey: "about-casi-learn-answer",
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                        to={RouteUtils.getUrl(siteMap.support.faq)}
                    />
                ),
            },
        },
    ];

    const heading = (
        <div className="about-casi-modal__heading">
            <div className="about-casi-modal__heading__logo" />
            <span>{label}</span>
        </div>
    );

    const { inMobileView } = useBreakpointRanges();

    return (
        <Modal
            cssClassName="about-casi-modal"
            closeDialog={closeDialog}
            isVisible={isVisible}
            label={label}
            type={inMobileView ? ModalTypes.Bottom : ModalTypes.Base}>
            <BaseModalHeader heading={heading} closeDialog={closeDialog} />
            <div className="about-casi-modal__content">
                <Trans
                    i18nKey={"about-casi-answer"}
                    components={{
                        p: <Paragraph cssClassName="-info" />,
                        sup: <sup className={TRADEMARK_CLASS_NAME} />,
                    }}
                    values={{ casi: Casi, nfpa: NfpaWithHtml }}
                />
                {renderPanels(panelConfigs)}
            </div>

            {!inMobileView && (
                <BaseModalFooter
                    primaryButtonProps={{ onClick: closeDialog }}
                    primaryText={t("close")}
                    closeDialog={closeDialog}
                />
            )}
        </Modal>
    );
};

export default AboutCasiModal;
